// Landing screen: brand, the "Ready to Chat" call to action, the Birth Year age
// gate, a mode toggle, prominent safety messaging, and a clear warning. ASCII
// only. See docs/BRANDING.md and docs/research/03-child-safety-legal.md.

import { t } from '../i18n.js';
import { el, ORB_SVG } from '../dom.js';
import { ChatMode, ageFromBirthYear, MIN_AGE, checkImpersonation, IMPERSONATION_MESSAGE } from '@lumi/shared';
import { LANDING_INFO_HTML } from './landing-info.js';
import { mountRoomCounts } from './room-counts.js';
import { SERVER_HTTP } from '../config.js';
import { getStoredAge } from '../age.js';
import { getRememberedBand, landingMinor } from '../band-memory.js';

/** The site's footer, the same seven links the static pages carry. */
export const LANDING_FOOTER = Object.freeze([
  { href: '/', label: 'Home' },
  { href: '/about', label: 'About' },
  { href: '/faq', label: 'FAQ' },
  { href: '/safety', label: 'Safety' },
  { href: '/parents', label: 'For parents' },
  { href: '/watch-together', label: 'Watch together' },
  { href: '/plus.html', label: 'Lumi Plus' },
]);

export function renderLanding({ onStart, onPlus, onAccount, onRegulars = null, invite = null, accountName = null, isStaff = () => false }) {
  // A Lumi Link to a known room decides the kind of chat: a text room is
  // joined as text, and nobody is asked for a camera on the way in. The
  // toggle is replaced by a line saying what the room is. Only a link to a
  // video room, or an ordinary start, ever asks for camera and microphone.
  const roomMode = invite?.room?.mode === ChatMode.TEXT || invite?.room?.mode === ChatMode.AUDIO || invite?.room?.mode === ChatMode.VIDEO
    ? invite.room.mode
    : null;
  let mode = roomMode || ChatMode.VIDEO;
  const currentYear = new Date().getUTCFullYear();

  const errorLine = el('div', { class: 'error', role: 'alert' });

  // Optional display handle -- what other people see you called.
  const handleInput = el('input', {
    type: 'text',
    id: 'handle',
    maxlength: '24',
    placeholder: t('Optional: what should people call you?'),
    'aria-label': 'Display name (optional)',
  });

  // Optional interests -- used only when interest matching is on, to prefer a similar
  // partner. Free text, e.g. "music, hiking, anime".
  const interestInput = el('input', {
    type: 'text',
    id: 'interests',
    maxlength: '120',
    placeholder: t('Optional: interests (e.g. music, hiking, anime)'),
    'aria-label': 'Interests (optional)',
  });

  // Birth Year dropdown.
  const yearSelect = el('select', { id: 'birthYear', 'aria-label': 'Birth year' }, [
    el('option', { value: '', text: t('Select year') }),
  ]);
  for (let y = currentYear; y >= currentYear - 100; y -= 1) {
    yearSelect.append(el('option', { value: String(y), text: String(y) }));
  }

  // Remember the handle + birth year so a returning user (e.g. after leaving a chat)
  // does not re-enter them. The age proof itself persists separately (see age.js).
  // A signed-in person gets their ACCOUNT NAME filled in -- their choice to show
  // it, not ours: clearing the field is remembered (the key is stored empty
  // rather than deleted), so a deliberate blank is never re-populated.
  let prefilledFromAccount = false;
  try {
    const savedHandle = localStorage.getItem('lumi.handle');
    const savedYear = localStorage.getItem('lumi.birthYear');
    const savedInterest = localStorage.getItem('lumi.interest');
    if (savedHandle !== null) {
      handleInput.value = savedHandle; // includes a deliberate empty string
    } else if (accountName) {
      handleInput.value = accountName;
      prefilledFromAccount = true;
    }
    if (savedInterest) interestInput.value = savedInterest;
    if (savedYear && [...yearSelect.options].some((o) => o.value === savedYear)) yearSelect.value = savedYear;
  } catch {
    /* localStorage unavailable -- no pre-fill */
  }

  // Mode toggle.
  const modeButtons = [
    [ChatMode.VIDEO, t('Video')],
    [ChatMode.AUDIO, t('Audio')],
    [ChatMode.TEXT, t('Text')],
  ].map(([value, label]) =>
    el('button', {
      type: 'button',
      text: label,
      aria: { pressed: value === mode ? 'true' : 'false' },
      onClick: (e) => {
        mode = value;
        modeButtons.forEach((b) =>
          b.setAttribute('aria-pressed', b === e.currentTarget ? 'true' : 'false'),
        );
        syncPrefRows();
      },
    }),
  );

  // ---- Lower-pressure starts: two remembered preferences ----
  // Camera: in a video chat the cameras open only once both people have
  // tapped "Show my camera" (the default), or -- for adults -- from the start,
  // which just sends that tap for you on every match. Ten minutes: a chat
  // with a clock and a gentle end, paired only with people who chose it too.
  // Both are remembered per browser; neither says anything about you to
  // anybody else. The camera choice shows only for Video, and "from the
  // start" is hidden for a birth year that makes you under 18 -- the app
  // checks the band again after the age check, so this is convenience, not
  // the rule.
  // Switched off by the operator -> the control never appears. The switches
  // are read after the screen draws, so both start OFF and both controls start
  // HIDDEN: a control that paints and then vanishes reads as a broken page,
  // and was on screen long enough to be tapped. A failed fetch leaves them
  // hidden -- the server ignores the choice when a switch is off anyway.
  let features = { timedChats: false, cameraChoice: false };
  let featuresKnown = false; // a remembered choice is kept, not dropped, until the server has answered
  let cameraStart = 'agree';
  let timed = false;
  try {
    cameraStart = localStorage.getItem('lumi.cameraStart') === 'open' ? 'open' : 'agree';
    timed = localStorage.getItem('lumi.timed') === '1';
  } catch {
    /* defaults */
  }
  const cameraButtons = [
    ['agree', t('Opens when we both agree')],
    ['open', t('Opens from the start')],
  ].map(([value, label]) =>
    el('button', {
      type: 'button',
      text: label,
      dataset: { value },
      aria: { pressed: value === cameraStart ? 'true' : 'false' },
      onClick: () => {
        cameraStart = value;
        cameraButtons.forEach((b) => b.setAttribute('aria-pressed', b.dataset.value === value ? 'true' : 'false'));
      },
    }),
  );
  const cameraRow = el('div', { class: 'pref-row camera-start hidden' }, [
    el('label', { text: t('Camera') }),
    el('div', { class: 'mode-toggle mode-toggle-small' }, cameraButtons),
  ]);
  const timedBtn = el(
    'button',
    {
      type: 'button',
      class: 'timed-toggle hidden',
      role: 'switch',
      aria: { checked: timed ? 'true' : 'false' },
      onClick: () => {
        timed = !timed;
        timedBtn.setAttribute('aria-checked', timed ? 'true' : 'false');
      },
    },
    [
      el('span', { class: 'timed-box', 'aria-hidden': 'true' }),
      el('span', { class: 'timed-label' }, [
        el('strong', { text: t('Ten minutes') }),
        el('span', { class: 'timed-hint', text: t('A chat with a gentle end, with people who chose it too.') }),
      ]),
    ],
  );
  function syncPrefRows() {
    // Switched off by the operator: the control goes, and the safer default
    // stands for everyone. A remembered choice is dropped too, or somebody who
    // once chose "from the start" would keep it invisibly after it was removed.
    if (featuresKnown && !features.cameraChoice && cameraStart !== 'agree') cameraStart = 'agree';
    const hideCamera = mode !== ChatMode.VIDEO || !features.cameraChoice;
    cameraRow.classList.toggle('hidden', hideCamera);
    // The ten-minute chat SPLITS THE POOL -- it only ever pairs with another
    // one -- so on a quiet site the operator turns it off, and then offering it
    // would just be a way to wait forever.
    timedBtn.classList.toggle('hidden', !features.timedChats);
    if (featuresKnown && !features.timedChats && timed) {
      timed = false;
      timedBtn.setAttribute('aria-checked', 'false');
    }
    // A minor by ANY signal: the typed year, the age check's verdict (the
    // stored token), or the band the server last put this browser in -- which
    // is where an administrator's override shows up. The typed year alone
    // offered "from the start" to somebody placed with the minors.
    const age = ageFromBirthYear(Number(yearSelect.value), currentYear);
    const minor = landingMinor({ age, storedBand: getStoredAge()?.band || null, rememberedBand: getRememberedBand() });
    cameraButtons[1].classList.toggle('hidden', minor);
    // A remembered "from the start" for someone now under 18 falls back quietly.
    if (minor && cameraStart === 'open') {
      cameraStart = 'agree';
      cameraButtons.forEach((b) => b.setAttribute('aria-pressed', b.dataset.value === 'agree' ? 'true' : 'false'));
    }
  }
  yearSelect.addEventListener('change', syncPrefRows);
  syncPrefRows();

  // Ask what is switched on, then show what is. Only a switch the server says
  // is ON brings its control out; anything else stays hidden.
  fetch(`${SERVER_HTTP}/api/features`)
    .then((r) => (r.ok ? r.json() : null))
    .then((f) => {
      if (!f) return;
      features = { timedChats: f.timedChats === true, cameraChoice: f.cameraChoice === true };
      featuresKnown = true;
      syncPrefRows();
    })
    .catch(() => {
      /* stays hidden: a choice the server would ignore is not worth showing */
    });

  function validate() {
    const birthYear = Number(yearSelect.value);
    if (!yearSelect.value || !Number.isInteger(birthYear)) {
      errorLine.textContent = 'Please choose your birth year to continue.';
      yearSelect.focus();
      return null;
    }
    const age = ageFromBirthYear(birthYear, currentYear);
    if (age !== null && age < MIN_AGE) {
      errorLine.textContent = `You must be at least ${MIN_AGE} years old to use Lumi.`;
      yearSelect.focus();
      return null;
    }
    // Names that pose as Lumi staff are refused here for an immediate answer;
    // the server enforces the same rule, so this is convenience, not security.
    // Staff are not posing, and the server lets them through, so neither does this.
    if (handleInput.value.trim() && !isStaff() && checkImpersonation(handleInput.value).blocked) {
      errorLine.textContent = IMPERSONATION_MESSAGE;
      handleInput.focus();
      return null;
    }
    errorLine.textContent = '';
    return birthYear;
  }

  function go(chatIntent, btn, label) {
    const birthYear = validate();
    if (birthYear === null) return;
    // Persist for next time (pre-filled on return).
    try {
      localStorage.setItem('lumi.birthYear', String(birthYear));
      // Store even an empty name: blank is a CHOICE to stay anonymous, and
      // deleting the key would just refill it from the account next time.
      localStorage.setItem('lumi.handle', handleInput.value.trim());
      const it = interestInput.value.trim();
      if (it) localStorage.setItem('lumi.interest', it);
      else localStorage.removeItem('lumi.interest');
      localStorage.setItem('lumi.cameraStart', cameraStart);
      localStorage.setItem('lumi.timed', timed ? '1' : '0');
    } catch {
      /* ignore */
    }
    [startBtn, groupBtn, joinRoomBtn].filter(Boolean).forEach((b) => {
      b.disabled = true;
    });
    btn.textContent = 'Connecting...';
    onStart({
      birthYear,
      mode,
      intent: chatIntent,
      handle: handleInput.value.trim() || undefined,
      interest: interestInput.value.trim() || undefined,
      // What is handed on honours the switch, whatever the browser remembers:
      // a hidden control must not carry a value.
      cameraStart: features.cameraChoice ? cameraStart : 'agree',
      timed: features.timedChats && timed,
    }).catch((err) => {
      [startBtn, groupBtn, joinRoomBtn].filter(Boolean).forEach((b) => {
        b.disabled = false;
      });
      btn.textContent = label;
      errorLine.textContent = err?.message || 'Something went wrong. Please try again.';
    });
  }

  const startBtn = el('button', {
    class: 'btn btn-primary',
    type: 'button',
    text: t('Ready to Chat'),
    onClick: () => go('1on1', startBtn, 'Ready to Chat'),
  });
  // Red against the violet of "Ready to Chat": the two are a choice between two
  // different evenings, not a main action and a lesser one, and a translucent
  // grey button reads as the afterthought.
  const groupBtn = el('button', {
    class: 'btn btn-pill-red',
    type: 'button',
    text: t('Group rooms'),
    onClick: () => go('group', groupBtn, 'Group rooms'),
  });
  // Shown only when the user arrived via an invite link: /r/CODE joins a room
  // once the age is confirmed; /c/CODE (kind 'community') only gets as far as
  // ASKING to join, because a community's members are let in by its host.
  const communityInvite = !!invite && invite.kind === 'community';
  // Somebody who verified on this device within the last two hours is not
  // going to be asked for a selfie, so the button must not say they will be.
  const verified = !!getStoredAge();
  const joinLabel = communityInvite
    ? (verified ? 'Ask to join' : 'Verify & ask to join')
    : (verified ? 'Join the room' : 'Verify & join the room');
  const joinRoomBtn = el('button', {
    class: 'btn btn-primary',
    type: 'button',
    text: joinLabel,
    onClick: () => go('group', joinRoomBtn, joinLabel),
  });
  const inviteBanner = invite
    ? el('div', { class: 'invite-banner' }, communityInvite
        ? [
            el('strong', { text: t('You are invited to a community.') }),
            el('span', {
              text: verified
                ? t(' You are already verified on this device. Its host lets people in when they see the request.')
                : t(' Verify your age to ask to join. Its host lets people in when they see the request.'),
            }),
          ]
        : [
            el('strong', { text: invite.room?.name ? `You are invited to ${invite.room.name}` : t('You are invited to a group room') }),
            el('span', {
              text: [
                roomMode === ChatMode.TEXT
                  ? ' A text room: typing only, so no camera or microphone is needed.'
                  : roomMode === ChatMode.AUDIO
                    ? ' An audio room: voices only, no camera.'
                    : roomMode === ChatMode.VIDEO
                      ? ' A video room.'
                      : ` Room code ${invite.code}.`,
                verified
                  ? ' You are already verified on this device, so one tap takes you in.'
                  : ' Confirm your age to join.',
              ].join(''),
            }),
          ])
    : null;

  const gate = el('div', { class: 'gate' }, [
    ...(inviteBanner ? [inviteBanner] : []),
    el('div', {}, [
      el('label', { for: 'handle', text: t('Name (optional)') }),
      handleInput,
      el('div', {
        class: 'field-hint',
        id: 'handleHint',
        text: prefilledFromAccount
          ? 'This is your account name. Clear it to stay anonymous: it is only shown if you leave it here.'
          : '',
      }),
    ]),
    el('div', {}, [el('label', { for: 'interests', text: t('Interests (optional)') }), interestInput]),
    el('div', { class: 'row' }, [
      el('div', {}, [
        el('label', { for: 'birthYear', text: t('Birth year') }),
        yearSelect,
        // People were picking 18 because they wanted to MEET eighteen-year-olds,
        // which is not what this field does. Two costs to that. The obvious one
        // is the disappointment. The other is the review queue: the birth year
        // is half the age decision now, so a forty-year-old who enters 18 reads
        // as a twenty-two-year gap between the face and the claim, which is a
        // mismatch, which lands on the operator's desk. Saying what the field
        // is for is cheaper than reviewing the people who guessed wrong.
        //
        // It does NOT say "it does not choose who you meet", which would be
        // untrue: the year sets your band, and the bands never mix. What it is
        // not is a preference for who you meet INSIDE your band, which is the
        // thing people were using it as.
        el('div', {
          class: 'field-hint',
          id: 'birthYearHint',
          text: t('Your age is never shown to anyone. It sets which age group you chat in, not who you meet in it.'),
        }),
      ]),
    ]),
    roomMode
      ? el('div', {}, [
          el('label', { text: t('How you will chat') }),
          el('p', {
            class: 'muted room-kind',
            text: roomMode === ChatMode.TEXT
              ? t('By text. This room has no camera or microphone.')
              : roomMode === ChatMode.AUDIO
                ? t('By voice. This room has no camera.')
                : t('By video, once you are in. Cameras open on your terms.'),
          }),
          cameraRow,
        ])
      : el('div', {}, [
          el('label', { text: t('How do you want to chat?') }),
          el('div', { class: 'mode-toggle' }, modeButtons),
          cameraRow,
          timedBtn,
        ]),
    invite
      ? el('div', { class: 'cta-row' }, [joinRoomBtn])
      : el('div', { class: 'cta-row' }, [startBtn, groupBtn]),
    errorLine,
  ]);

  const safetyStrip = el('div', { class: 'safety-strip' }, [
    el('span', { class: 'pill' }, [el('span', { class: 'dot' }), 'Safe by design']),
    el('span', { class: 'pill' }, [el('span', { class: 'dot' }), 'No account needed']),
    el('span', { class: 'pill' }, [el('span', { class: 'dot' }), 'Your IP stays hidden']),
    el('span', { class: 'pill' }, [el('span', { class: 'dot' }), 'Report anytime']),
  ]);

  // The rooms the site keeps open, with live headcounts: "14 people in the
  // quiet room right now". Filled, or left hidden, by room-counts.js from
  // the list the socket pushes. Nothing is drawn until the first list, so a
  // slow socket shows no line rather than a wrong one.
  const openRooms = el('div', { id: 'openRooms', class: 'open-rooms', hidden: '' });
  // Kept on the node so app.js can hand it a pushed list without the landing
  // and the socket having to know about each other.
  openRooms.__roomCounts = mountRoomCounts(openRooms);

  const legal = el('div', { class: 'legal' }, [
    'By tapping Ready to Chat you confirm you are at least 13 years old and agree to ',
    'be paired with a stranger. Be kind. Anything you show on camera could be recorded ',
    'by the other person, so never share anything you would not want seen. Nudity, ',
    'harassment, and any content involving minors are forbidden and reported. Under-18 ',
    'users are only matched with other under-18 users.',
  ]);

  return el('div', { class: 'landing' }, [
    // First in the tab order, and invisible until it is focused: four nav
    // links stand between the top of every page and what the page is about.
    el('a', { class: 'skip-link', href: '#main', text: t('Skip to the content') }),
    el('div', { class: 'landing-inner' }, [
      el('nav', { class: 'topbar', aria: { label: 'Site' } }, [
        el('div', { class: 'topbar-brand' }, [el('span', { html: ORB_SVG }), 'Lumi']),
        el('div', { class: 'topbar-actions' }, [
          // Signing in is its OWN door. It used to live inside the Lumi Plus
          // modal, which made having an account look like a paid thing.
          // app.js relabels this in place once the account fetch returns.
          el('button', {
            id: 'accountLink',
            class: 'btn btn-ghost btn-sm account-link',
            text: accountName ? `@${accountName}` : t('Sign in'),
            title: accountName ? t('Your account') : t('Sign in or create an account'),
            onClick: () => onAccount?.(),
          }),
          // How many other people are on the site right now. Hidden until the
          // server says there is somebody, because "0 other people here" is the
          // one thing a quiet site must never say out loud.
          el('span', { id: 'lobbyCount', class: 'lobby-count', hidden: '' }),
          // "Regulars (N)", once a person has any. Hidden until app.js has asked
          // the server how many -- there is no socket on the landing, so the
          // count arrives over HTTP after this renders, and a link to an empty
          // list would be a promise the page cannot keep.
          el('button', {
            id: 'regularsLink',
            class: 'btn btn-ghost btn-sm regulars-link',
            text: t('Regulars'),
            title: t('Your regulars'),
            hidden: '',
            onClick: () => onRegulars?.(),
          }),
          el('a', { class: 'btn btn-ghost btn-sm plus-link', href: '/about', text: t('About') }),
          el('a', { class: 'btn btn-ghost btn-sm plus-link', href: '/faq', text: t('FAQ') }),
          el('a', { class: 'btn btn-ghost btn-sm plus-link', href: '/watch-together', text: t('Watch together') }),
          el('a', { class: 'btn btn-ghost btn-sm plus-link', href: '/plus.html', text: t('Lumi Plus') }),
        ]),
      ]),
      el('main', { class: 'hero', id: 'main' }, [
        el('div', { class: 'orb', html: ORB_SVG }),
        el('h1', { text: t('Light up a conversation') }),
        el('p', {
          class: 'tagline',
          text: t('Free, anonymous video, audio and text chat. Meet someone new one to one, or open a room and bring your friends.'),
        }),
        gate,
        safetyStrip,
        openRooms,
      ]),
      legal,
      el('section', { class: 'landing-info', html: LANDING_INFO_HTML }),
      // The same seven links every static page carries. The landing had
      // none, and its top bar drops two of its four links off a phone, so
      // on a phone there was no route from the front page to /about, /faq,
      // or /watch-together at all.
      el('footer', { class: 'landing-footer' }, [
        LANDING_FOOTER.flatMap((link, i) => [
          i ? el('span', { class: 'landing-footer-sep', text: '·' }) : null,
          // The page you are on is marked, so a screen reader says "current
          // page" rather than reading it as one more place to go.
          el('a', {
            href: link.href,
            text: t(link.label),
            ...(link.href === '/' ? { aria: { current: 'page' } } : {}),
          }),
        ].filter(Boolean)),
      ].flat()),
    ]),
  ]);
}
